import { useCallback, useMemo, useRef, useState } from "react";
import { Modal, Progress, Button } from "antd";

/**
 * usePdfGeneration
 *
 * Manages:
 * - progress modal (during capture)
 * - blob-url preview modal with <iframe>
 * - download/open-in-new-tab actions
 * - callbacks to wire into a collated PDF renderer (ReportTemplateCollatedDirect)
 */
export function usePdfGeneration({
  previewTitle = "PDF Preview",
  progressTitle = "Generating PDF…",
  defaultFilename = `Report_${new Date().toISOString().slice(0, 10)}.pdf`,
  onPreviewClose,
  autoOpenProgress = true,
} = {}) {
  // ----- State
  const [progressVisible, setProgressVisible] = useState(false);
  const [progressCount, setProgressCount] = useState(0);
  const [progressTotal, setProgressTotal] = useState(0);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");

  // ⭐ NEW: Per-run filename override
  const [overrideFilename, setOverrideFilename] = useState(null);

  // Guard to avoid re-emitting same blob URL multiple times
  const blobEmittedRef = useRef(false);

  // ----- Controls
  const openProgress = useCallback((expectedCount) => {
    setProgressTotal(typeof expectedCount === "number" ? expectedCount : 0);
    setProgressCount(0);
    setProgressVisible(true);
  }, []);

  const closeProgress = useCallback(() => {
    setProgressVisible(false);
  }, []);

  const openPreview = useCallback((url) => {
    if (url) {
      setPreviewUrl(url);
      setPreviewVisible(true);
    }
  }, []);

  const closePreview = useCallback(() => {
    setPreviewVisible(false);
    setProgressVisible(false);
    blobEmittedRef.current = false;

    // ⭐ Reset filename override after closing
    setOverrideFilename(null);

    if (typeof onPreviewClose === "function") onPreviewClose();
  }, [onPreviewClose]);

  const reset = useCallback(() => {
    setProgressVisible(false);
    setPreviewVisible(false);
    setProgressCount(0);
    setProgressTotal(0);
    setPreviewUrl("");

    blobEmittedRef.current = false;

    // ⭐ Reset filename override on reset
    setOverrideFilename(null);

    if (typeof onPreviewClose === "function") onPreviewClose();
  }, [onPreviewClose]);

  // ⭐⭐⭐ FULL UPDATED getCollatedCallbacks (STEP 2 COMPLETE) ⭐⭐⭐
  const getCollatedCallbacks = useCallback(
    (extra = {}) => {
      const {
        onReady: userOnReady,
        onBlobReady: userOnBlobReady,
        filename: userFilename,
      } = extra || {};

      // ⭐ Accept filename override from parent
      if (userFilename) {
        setOverrideFilename(userFilename);
      }

      return {
        onProgress: (done, total) => {
          if (
            autoOpenProgress &&
            !progressVisible &&
            total > 0 &&
            !blobEmittedRef.current
          ) {
            setProgressTotal(total);
            setProgressVisible(true);
          }
          if (typeof total === "number") setProgressTotal(total);
          if (typeof done === "number") setProgressCount(done);
        },

        onReady: (...args) => {
          closeProgress();
          if (typeof userOnReady === "function") userOnReady(...args);
        },

        onBlobReady: (url, blob, ...rest) => {
          closeProgress();

          if (!blobEmittedRef.current && url) {
            blobEmittedRef.current = true;
            openPreview(url);
          }

          if (typeof userOnBlobReady === "function") {
            userOnBlobReady(url, blob, ...rest);
          }
        },
      };
    },
    [
      autoOpenProgress,
      progressVisible,
      closeProgress,
      openPreview,
      setOverrideFilename,
    ]
  );

  // ----- UI (Progress Modal + Preview Modal)

  const ProgressModal = useMemo(
    () => (
      <Modal
        open={progressVisible}
        footer={null}
        closable={false}
        maskClosable={false}
        centered
        bodyStyle={{ textAlign: "center", padding: 24 }}
      >
        <h3 style={{ marginBottom: 16 }}>{progressTitle}</h3>
        <Progress
          type="circle"
          percent={
            progressTotal > 0
              ? Math.round((progressCount / progressTotal) * 100)
              : 0
          }
          status={
            progressTotal > 0 && progressCount === progressTotal
              ? "success"
              : "normal"
          }
        />
        <div style={{ marginTop: 12, color: "#666" }}>
          {progressTotal > 0
            ? `Captured ${progressCount} of ${progressTotal} chart${
                progressTotal > 1 ? "s" : ""
              }`
            : "Preparing charts…"}
        </div>
      </Modal>
    ),
    [progressVisible, progressCount, progressTotal, progressTitle]
  );

  const PreviewModal = useMemo(
    () => (
      <Modal
        open={previewVisible}
        width="90%"
        style={{ top: 24 }}
        bodyStyle={{ padding: 0 }}
        onCancel={closePreview}
        footer={[
          <Button
            key="open"
            onClick={() =>
              window.open(previewUrl, "_blank", "noopener,noreferrer")
            }
          >
            Open in new tab
          </Button>,

          // ⭐ UPDATED DOWNLOAD BUTTON (filename override applied)
          <Button
            key="download"
            type="primary"
            onClick={() => {
              if (!previewUrl) return;
              const a = document.createElement("a");
              a.href = previewUrl;

              // ⭐ Use overrideFilename when available
              a.download = overrideFilename || defaultFilename;

              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
            }}
          >
            Download PDF
          </Button>,

          <Button key="close" onClick={closePreview}>
            Close
          </Button>,
        ]}
      >
        <iframe
          title={previewTitle}
          src={previewUrl}
          style={{ border: "none", width: "100%", height: "80vh" }}
        />
      </Modal>
    ),
    [previewVisible, previewUrl, overrideFilename, defaultFilename, previewTitle, closePreview]
  );

  return {
    // UI
    ProgressModal,
    PreviewModal,

    // State
    progressVisible,
    previewVisible,
    progressCount,
    progressTotal,
    previewUrl,

    // Controls
    openProgress,
    closeProgress,
    openPreview,
    closePreview,
    reset,

    // PDF capture callbacks
    getCollatedCallbacks,
  };
}